"use client"

import { useCallback, useEffect } from "react"
import { X, Palette, Columns3, Settings2, Filter, ChevronLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/use-translation"
import { useToast } from "@/hooks/use-toast"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { ViewManagerProvider, useViewManager, type ViewManagerStep } from "./view-manager-context"
import { ViewsStep } from "./steps/views-step"
import { ColumnsStep } from "./steps/columns-step"
import { FieldsStep } from "./steps/fields-step"
import { FiltersStep } from "./steps/filters-step"
import { fieldEditDirtyRef } from "./panels/field-edit-panel"
import { CustomFieldsStep } from "./steps/custom-fields-step"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useState } from "react"

// ─── Edit Step Configuration (shown only when a view is selected) ────

interface EditStepConfig {
  id: ViewManagerStep
  labelKey: string
  fallbackLabel: string
  icon: React.ReactNode
}

const EDIT_STEPS: EditStepConfig[] = [
  { id: 'columns', labelKey: 'vmStepColumns', fallbackLabel: 'Columns', icon: <Columns3 size={16} /> },
  { id: 'fields', labelKey: 'vmStepFields', fallbackLabel: 'Fields', icon: <Settings2 size={16} /> },
  { id: 'filters', labelKey: 'vmStepFilters', fallbackLabel: 'Filters', icon: <Filter size={16} /> },
]

// ─── Inner Content (requires ViewManagerProvider) ────────────────────

function ViewManagerContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const { state, dispatch, isDirty } = useViewManager()
  const { refreshViews, configuration: globalConfig } = useWorkflow()
  const { saveViewSettings, fetchView } = useWorkflowApi()
  const { toast } = useToast()
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)

  // Permissions — use global configuration (always available) rather than view-specific config
  const authorizedActions = (globalConfig?.authorizedActions || state.originalConfig?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const canEditColumns = authorizedActions.includes("EditColumns")

  const hasView = !!state.selectedView
  const isInEditMode = hasView && state.activeStep !== 'views'

  // Handle close with unsaved changes check (including field edit panel)
  const handleClose = useCallback(() => {
    if (fieldEditDirtyRef.current) {
      const confirmed = window.confirm('You have unsaved field changes. Discard them?')
      if (!confirmed) return
      fieldEditDirtyRef.current = false
    }
    if (isDirty) {
      setShowDiscardDialog(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  // Back to views list — check for unsaved changes (view + field edits)
  const handleBackToViews = useCallback(() => {
    if (fieldEditDirtyRef.current) {
      const confirmed = window.confirm('You have unsaved field changes. Discard them?')
      if (!confirmed) return
      fieldEditDirtyRef.current = false
    }
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Going back will discard them. Continue?')
      if (!confirmed) return
    }
    // Reload the view to revert draft to original
    if (state.selectedView && state.originalConfig) {
      dispatch({
        type: 'LOAD_VIEW',
        payload: {
          view: state.selectedView,
          config: state.originalConfig,
          authorizedClients: state.originalAuthorizedClients,
        }
      })
    }
    dispatch({ type: 'SET_STEP', payload: 'views' })
  }, [isDirty, dispatch, state.selectedView, state.originalConfig, state.originalAuthorizedClients])

  // Revert all changes to original
  const handleRevert = useCallback(() => {
    if (!state.selectedView || !state.originalConfig) return
    const confirmed = window.confirm(
      'Revert all changes to the last saved state?'
    )
    if (!confirmed) return
    dispatch({
      type: 'LOAD_VIEW',
      payload: {
        view: state.selectedView,
        config: state.originalConfig,
        authorizedClients: state.originalAuthorizedClients,
      }
    })
  }, [dispatch, state.selectedView, state.originalConfig, state.originalAuthorizedClients])

  // Keyboard: Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (isInEditMode) {
          handleBackToViews()
        } else {
          handleClose()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleClose, handleBackToViews, isInEditMode])

  // Handle Save — single API call, stays in designer
  const handleSave = useCallback(async () => {
    if (!state.selectedView) return

    dispatch({ type: 'SET_SAVING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })

    try {
      const visibleInApplications = state.draftIsClientFacing ? ['Client'] : []

      // Determine what changed to send the most efficient request
      const hasMetaChanges =
        state.draftViewTitle !== state.selectedView.title ||
        state.draftIsPublic !== (state.selectedView.isPublic ?? false) ||
        state.draftIsClientFacing !== (state.selectedView.isClientFacing ?? false) ||
        // AllowDataExport lives in the View Properties panel, so a change
        // to it counts as a metadata-only edit when nothing else differs.
        // Treating it as meta keeps the request light when admins are just
        // toggling export on/off without otherwise touching columns/filters.
        state.draftAllowDataExport !== (state.selectedView.allowDataExport ?? false) ||
        JSON.stringify(state.draftAuthorizedClients.map(c => c.entityId).sort()) !==
          JSON.stringify(state.originalAuthorizedClients.map(c => c.entityId).sort())

      // Check if columns or filters changed
      let hasStructuralChanges = false
      const originalStages = state.originalConfig?.stageConfigurations || []
      for (const stage of originalStages) {
        const stageId = stage.stageId || stage.stageCode
        const originalIds = (stage.fieldConfigurations || []).map((f: any) => f.fieldConfigurationId)
        const draftIds = state.draftStageColumns[stageId] || []
        if (JSON.stringify(originalIds) !== JSON.stringify(draftIds)) { hasStructuralChanges = true; break }
      }
      if (JSON.stringify(state.draftGlobalFilters) !== JSON.stringify(state.originalConfig?.savedGlobalFilters || [])) hasStructuralChanges = true
      if (JSON.stringify(state.draftStageFilters) !== JSON.stringify(state.originalConfig?.savedStageFilters || [])) hasStructuralChanges = true
      // Check sort changes
      if (!hasStructuralChanges) {
        for (const stage of originalStages) {
          const sid = stage.stageId || stage.stageCode
          const origSort = stage.sortColumn && stage.sortDirection ? { columnCode: stage.sortColumn, direction: stage.sortDirection } : null
          const draftSort = state.draftSortByStage?.[sid] || null
          if (JSON.stringify(origSort) !== JSON.stringify(draftSort)) { hasStructuralChanges = true; break }
        }
      }

      const metaOnlyChanged = hasMetaChanges && !hasStructuralChanges

      // Build authorized client list for the request
      const authorizedClientList = state.draftAuthorizedClients.map(c => ({
        entityId: c.entityId,
        name: c.name,
      }))

      if (metaOnlyChanged) {
        // Only metadata changed — lightweight title/visibility update
        await saveViewSettings({
          id: state.selectedView.viewSettingsId,
          title: state.draftViewTitle,
          isPublic: state.draftIsPublic,
          // Forward the per-view export opt-in. BE updates this on both
          // the title/visibility branch and the full-save branch, so it
          // travels cleanly with either request shape.
          allowDataExport: state.draftAllowDataExport,
          isTitleAndVisibilityUpdate: true,
          isUserViewStateUpdate: false,
          updateFilters: false,
          stageConfigurations: [],
          stageFilters: [],
          globalFilters: [],
          visibleInApplications,
          authorizedClientList,
        })
      } else {
        // Full save — stage configs, filters, and metadata in one request
        const stageConfigurations = Object.entries(state.draftStageColumns).map(
          ([stageId, fieldIds]) => ({
            stageConfigurationId: stageId,
            fieldConfigurationIds: fieldIds,
            sortColumn: state.draftSortByStage?.[stageId]?.columnCode || null,
            sortDirection: state.draftSortByStage?.[stageId]?.direction || null,
          })
        )

        await saveViewSettings({
          id: state.selectedView.viewSettingsId,
          title: state.draftViewTitle,
          isDefault: state.selectedView.isDefault ?? false,
          isPublic: state.draftIsPublic,
          allowDataExport: state.draftAllowDataExport,
          isTitleAndVisibilityUpdate: false,
          isUserViewStateUpdate: false,
          updateFilters: true,
          stageConfigurations,
          stageFilters: state.draftStageFilters,
          globalFilters: state.draftGlobalFilters,
          columnWidths: JSON.stringify(state.draftColumnWidths),
          visibleInApplications,
          authorizedClientList,
        })
      }

      // TODO: Save field property changes (PUT /field/{id} for each modified field) — Phase 4

      // Refresh views list silently (don't close designer)
      await refreshViews()

      // Reload the view using the lightweight GET /views/{id} endpoint (not GET /configurations)
      const rawView = await fetchView(state.selectedView.viewSettingsId)

      // Build config from the raw view + existing global configuration (same pattern as views-step.tsx)
      // Fall back to the current designer snapshot when globalConfig is null (e.g. after first save)
      const baseConfig = globalConfig || state.originalConfig
      if (baseConfig) {
        const allFieldsMap = new Map<string, any>()
        for (const f of (baseConfig.fieldsInUse || [])) allFieldsMap.set(f.fieldConfigurationId, f)
        for (const stage of (baseConfig.stageConfigurations || [])) {
          for (const f of (stage.fieldConfigurations || [])) allFieldsMap.set(f.fieldConfigurationId, f)
        }

        const rawStageConfigs = rawView?.stageConfigurations || rawView?.StageConfigurations || []
        const stageConfigurations = (baseConfig.stageConfigurations || []).map((baseStage: any) => {
          const stageId = baseStage.stageId || baseStage.stageCode
          const rawStage = rawStageConfigs.find(
            (rs: any) => (rs.stageConfigurationId || rs.StageConfigurationId) === stageId
          )
          const rawFieldIds: string[] = rawStage?.fieldConfigurationIds || rawStage?.FieldConfigurationIds || []

          return {
            ...baseStage,
            fieldConfigurations: rawFieldIds.map((id: string) => allFieldsMap.get(id)).filter(Boolean),
            sortColumn: rawStage?.sortColumn || rawStage?.SortColumn || null,
            sortDirection: rawStage?.sortDirection || rawStage?.SortDirection || null,
          }
        })

        const freshConfig = {
          ...baseConfig,
          stageConfigurations,
          viewSettingsId: state.selectedView.viewSettingsId,
          savedGlobalFilters: rawView?.globalFilters || rawView?.GlobalFilters || [],
          savedStageFilters: rawView?.stageFilters || rawView?.StageFilters || [],
          columnWidths: rawView?.columnWidths || rawView?.ColumnWidths || baseConfig.columnWidths,
        }

        const rawClients = rawView?.authorizedClientList || rawView?.AuthorizedClientList || []
        const authorizedClients = rawClients.length > 0
          ? rawClients.map((c: any) => ({ entityId: c.entityId ?? c.EntityId, name: c.name ?? c.Name }))
          : state.draftAuthorizedClients

        dispatch({
          type: 'LOAD_VIEW',
          payload: {
            view: { ...state.selectedView, title: state.draftViewTitle, isPublic: state.draftIsPublic, isClientFacing: state.draftIsClientFacing, allowDataExport: state.draftAllowDataExport },
            config: freshConfig,
            authorizedClients,
          },
        })
      }

      toast({
        variant: "success",
        title: t('success') || "Success",
        description: t('viewSavedSuccessfully') || "View saved successfully",
      })
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        payload: err instanceof Error ? err.message : 'Failed to save changes',
      })
    } finally {
      dispatch({ type: 'SET_SAVING', payload: false })
    }
  }, [state, dispatch, saveViewSettings, refreshViews, fetchView, globalConfig])

  const activeStep = state.activeStep

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-gray-200 bg-[#F9F9FF] flex-shrink-0">
        <div className="flex items-center gap-3">
          <Palette size={22} className="text-[#0F214B]" />
          <h1 className="text-xl font-semibold text-[#0F214B]">
            {t('vmTitle' as any) || 'View Designer'}
          </h1>
          {isInEditMode && state.selectedView && (
            <>
              <span className="text-gray-300 mx-1">/</span>
              <span className="text-base font-medium text-gray-600">{state.draftViewTitle || state.selectedView.title}</span>
            </>
          )}
          {!isInEditMode && activeStep !== 'custom-fields' && canEditColumns && (
            <>
              <div className="h-5 w-px bg-gray-300 ml-1" />
              <button
                onClick={() => dispatch({ type: 'SET_STEP', payload: 'custom-fields' })}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#0F214B] font-medium transition-colors"
              >
                <Settings2 size={15} />
                {t('manageCustomFields' as any) || 'Custom Fields'}
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
        >
          <X size={20} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* ── Edit Mode Navigation (only when editing a view) ── */}
      {isInEditMode && (
        <div className="flex items-center py-2 bg-[#FAFAFF] border-b border-gray-200 flex-shrink-0">
          <div className="w-52 flex items-center justify-center flex-shrink-0 border-r border-gray-200">
            <button
              onClick={handleBackToViews}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              <ChevronLeft size={16} />
              {t('vmBackToViews' as any) || 'Back to Views'}
            </button>
          </div>
          <div className="flex items-center gap-2 px-4">
          {EDIT_STEPS.map((step) => {
            const isActive = activeStep === step.id
            return (
              <button
                key={step.id}
                onClick={() => dispatch({ type: 'SET_STEP', payload: step.id })}
                className={`
                  flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-full transition-colors
                  ${isActive
                    ? 'bg-[#0F214B] text-white'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }
                `}
              >
                {step.icon}
                {t(step.labelKey as any) || step.fallbackLabel}
              </button>
            )
          })}
          {/* Custom Fields button */}
          {canEditColumns && (
            <>
              <div className="h-5 w-px bg-gray-300 ml-2" />
              <button
                onClick={() => dispatch({ type: 'SET_STEP', payload: 'custom-fields' })}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-200 transition-colors"
              >
                <Settings2 size={14} />
                {t('manageCustomFields' as any) || 'Custom Fields'}
              </button>
            </>
          )}
          {/* Revert button — only when dirty */}
          {isDirty && (
            <>
              <div className="h-5 w-px bg-gray-300 ml-2" />
              <button
                onClick={handleRevert}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-full text-red-500 hover:bg-red-50 border border-red-200 transition-colors"
              >
                {t('vmRevert' as any) || 'Revert Changes'}
              </button>
            </>
          )}
          </div>
        </div>
      )}

      {/* ── Step Content ── */}
      <div className="flex-1 overflow-hidden">
        {activeStep === 'views' && <ViewsStep />}
        {activeStep === 'columns' && <ColumnsStep />}
        {activeStep === 'fields' && <FieldsStep />}
        {activeStep === 'filters' && <FiltersStep />}
        {activeStep === 'custom-fields' && <CustomFieldsStep />}
      </div>

      {/* ── Footer — hidden when field edit panel is open (it has its own footer) ── */}
      {!state.selectedFieldId && (isInEditMode || isDirty) && (
      <div className="flex items-center justify-between px-8 py-4 border-t border-gray-200 bg-[#F9F9FF] flex-shrink-0">
        <div className="text-sm text-gray-500">
          {isDirty && (
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
              {t('unsavedChanges' as any) || 'You have unsaved changes'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isDirty && hasView && (
            <Button
              variant="elliptical-outline"
              onClick={handleRevert}
              disabled={state.isSaving}
              className="text-red-500 border-red-200 hover:bg-red-50"
            >
              {t('vmRevert' as any) || 'Revert Changes'}
            </Button>
          )}
          <Button
            variant="elliptical-outline"
            onClick={() => {
              if (fieldEditDirtyRef.current) {
                const confirmed = window.confirm('You have unsaved field changes. Discard them?')
                if (!confirmed) return
                fieldEditDirtyRef.current = false
              }
              if (isDirty) handleRevert()
              dispatch({ type: 'SET_STEP', payload: 'views' })
            }}
            disabled={state.isSaving}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          {hasView && (
            <Button
              variant="elliptical"
              onClick={handleSave}
              disabled={!isDirty || state.isSaving}
            >
              {state.isSaving
                ? (t('atsSyncSaving' as any) || 'Saving...')
                : (t('save') || 'Save')
              }
            </Button>
          )}
        </div>
      </div>
      )}

      {/* ── Discard Changes Dialog ── */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('vmDiscardTitle' as any) || 'Discard unsaved changes?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('vmDiscardDescription' as any) || 'You have unsaved changes that will be lost if you close the View Designer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="elliptical-outline">
              {t('vmKeepEditing' as any) || 'Keep editing'}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="elliptical"
              onClick={() => {
                dispatch({ type: 'RESET' })
                onClose()
              }}
            >
              {t('vmsDiscard' as any) || 'Discard'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  )
}

// ─── Public Component (wraps with Provider) ──────────────────────────

interface ViewManagerProps {
  onClose: () => void
}

export function ViewManager({ onClose }: ViewManagerProps) {
  return (
    <ViewManagerProvider>
      <ViewManagerContent onClose={onClose} />
    </ViewManagerProvider>
  )
}
